import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import Card from '../components/Card';
import { getRefPartyMonth, getRefPartyLines } from '../services/dashboardApi';

// REFPARTY-REPORT v1
// DRILL v3: customer lists show Size (not batch/shade); batch/shade only in the detail
// DRILL v2: day click = customer list (total) -> click a customer = detail with design + batch/shade;
// size / customer click = detail; customer table has batch/shade + grand total; design-wise has one tab per size
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PALETTE = ['#0E6F8A', '#B25C3F', '#3E72B0', '#237246', '#8F5F00', '#7A5C9E', '#5F6872', '#C0453B'];
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const dmy = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');
const tick = { fill: 'var(--color-text-secondary)', fontSize: 11 };
const TH = { textAlign: 'left', verticalAlign: 'bottom', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' };
const TF = { ...TD, fontWeight: 800, borderTop: '2px solid var(--color-border)' };
const FIELD = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
const HINT = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 4 };
const CLICKABLE = { cursor: 'pointer' };
const clip = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '');

function shift(ym, d) {
  const dt = new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1 + d, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function Stat({ label, value, sub }) {
  return (
    <Card style={{ marginBottom: 0 }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 'var(--font-size-xs)' }}>{sub}</div> : null}
    </Card>
  );
}

function DrillModal({ drill, month, party, onClose, onBack, onOpenCustomer }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const key = JSON.stringify([drill.group, drill.query]);
  useEffect(() => {
    let alive = true;
    setData(null); setErr('');
    getRefPartyLines({ month, party: party || undefined, group: drill.group, ...drill.query })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr((e && e.response && e.response.data && e.response.data.error) || 'Could not load.'); });
    return () => { alive = false; };
  }, [month, party, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const isSummary = drill.group === 'customer';
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ? data.rows : []).filter((r) => !s || `${r.customer} ${r.city} ${r.design || ''} ${r.size || ''} ${r.batch || r.sizes || ''}`.toLowerCase().includes(s));
  }, [data, q]);
  const shownTotal = rows.reduce((a, r) => a + r.boxes, 0);
  const multiDay = !drill.query.date;
  return (
    <div role="dialog" aria-modal="true" aria-label={drill.title} onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--color-surface)', color: 'var(--color-text-primary)', borderRadius: 14, width: 'min(1080px, 100%)', maxHeight: '92vh', overflow: 'auto', padding: 'var(--space-4)', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 18px)' }}>{drill.title}{data ? ` — ${fmt(data.total)} box` : ''}</h2>
          <span style={{ display: 'flex', gap: 8 }}>
            {onBack && <button type="button" className="btn btn-secondary" onClick={onBack}>◀ Back</button>}
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </span>
        </div>
        {err && <p style={{ color: 'var(--color-danger)' }}>{err}</p>}
        {!data && !err && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
        {data && isSummary && (
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / city / size" style={{ ...FIELD, width: '100%', maxWidth: 360, marginBottom: 8 }} />
            <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto' }} className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th>
                    <th style={TH}>Size</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Box</th><th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td style={TD} colSpan={6}>No dispatch.</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={r.customerId} style={CLICKABLE} title="Click to see this customer's dispatch in detail" onClick={() => onOpenCustomer(r)}>
                      <td style={TD}>{i + 1}</td>
                      <td style={{ ...TD, fontWeight: 600 }}>{r.customer}</td>
                      <td style={TD}>{r.city || '—'}</td>
                      <td style={TD} title={r.sizes}>{clip(r.sizes, 40) || '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(r.boxes)}</td>
                      <td style={{ ...TD, color: 'var(--color-text-muted)' }}>›</td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={TF} colSpan={4}>Grand Total ({rows.length} customers)</td>
                      <td style={{ ...TF, textAlign: 'right' }}>{fmt(shownTotal)}</td><td style={TF} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div style={HINT}>Click a customer to see that customer&apos;s dispatch in detail.</div>
          </>
        )}
        {data && !isSummary && (
          <>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer / design / size / batch" style={{ ...FIELD, width: '100%', maxWidth: 360, marginBottom: 8 }} />
            <div style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto' }} className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {multiDay && <th style={TH}>Date</th>}
                    <th style={TH}>Customer</th><th style={TH}>City</th>
                    {data.designsAvailable && <th style={TH}>Design</th>}
                    <th style={TH}>Size</th>
                    {data.hasBatch && <th style={TH}>Batch/Shade</th>}
                    <th style={{ ...TH, textAlign: 'right' }}>Box</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td style={TD} colSpan={7}>No dispatch.</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={`${r.date}-${r.customer}-${r.design}-${r.size}-${r.batch}-${i}`}>
                      {multiDay && <td style={TD}>{dmy(r.date)}</td>}
                      <td style={TD}>{r.customer}</td>
                      <td style={TD}>{r.city || '—'}</td>
                      {data.designsAvailable && <td style={TD}>{r.design}</td>}
                      <td style={TD}>{r.size}</td>
                      {data.hasBatch && <td style={TD}>{r.batch || '—'}</td>}
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(r.boxes)}</td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={TF} colSpan={(multiDay ? 1 : 0) + 2 + (data.designsAvailable ? 1 : 0) + 1 + (data.hasBatch ? 1 : 0)}>Grand Total ({rows.length} lines)</td>
                      <td style={{ ...TF, textAlign: 'right' }}>{fmt(shownTotal)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function RefPartyMonthPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const now = new Date();
  const thisYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const ym = /^\d{4}-(0[1-9]|1[0-2])$/.test(sp.get('month') || '') ? sp.get('month') : thisYm;
  const party = sp.get('party') || '';
  const monthName = `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [cq, setCq] = useState('');
  const [dq, setDq] = useState('');
  const [sizeTab, setSizeTab] = useState('');
  const [stack, setStack] = useState([]);
  const drill = stack.length ? stack[stack.length - 1] : null;
  const push = (d) => setStack((s) => [...s, d]);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(''); setSizeTab(''); setStack([]);
    getRefPartyMonth(ym, party || undefined)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr((e && e.response && e.response.data && e.response.data.error) || 'Could not load this month.'); });
    return () => { alive = false; };
  }, [ym, party]);

  // SIZE-CLICK v1: arriving from the dashboard size chart (?size=NAME) opens that size's detail once
  const sizeParam = sp.get('size') || '';
  useEffect(() => {
    if (!data || !sizeParam) return;
    const hit = data.sizes.find((x) => String(x.name).toLowerCase() === sizeParam.toLowerCase());
    if (hit) setStack([{ group: 'customer', title: `${hit.name} — customer-wise dispatch (${monthName})`, query: { sizeId: hit.id } }]);
    const n = new URLSearchParams(sp); n.delete('size'); setSp(n, { replace: true });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (m) => { const n = new URLSearchParams(sp); n.set('month', m); setSp(n, { replace: true }); };
  const title = data ? (data.name || (data.scope === 'own' ? 'My sales' : 'All Ref. Parties')) : 'Ref. Party sales';

  const customers = useMemo(() => {
    const q = cq.trim().toLowerCase();
    return (data ? data.customers : []).filter((c) => !q || `${c.name} ${c.city} ${c.sizes || ''}`.toLowerCase().includes(q));
  }, [data, cq]);
  const custBoxes = customers.reduce((a, c) => a + c.boxes, 0);
  const custLines = customers.reduce((a, c) => a + c.lines, 0);

  const sizeTabs = useMemo(() => {
    const m = new Map();
    (data ? data.designs : []).forEach((d) => {
      const k = String(d.sizeId);
      if (!m.has(k)) m.set(k, { id: k, name: d.size, count: 0, boxes: 0 });
      const e = m.get(k); e.count += 1; e.boxes += d.boxes;
    });
    return Array.from(m.values()).sort((a, b) => b.boxes - a.boxes);
  }, [data]);
  const designs = useMemo(() => {
    const q = dq.trim().toLowerCase();
    return (data ? data.designs : []).filter((d) => (!sizeTab || String(d.sizeId) === sizeTab) && (!q || d.name.toLowerCase().includes(q)));
  }, [data, dq, sizeTab]);
  const designBoxes = designs.reduce((a, d) => a + d.boxes, 0);

  const t = data ? data.totals : null;
  let delta = null;
  if (t && t.prevBoxes > 0) {
    const pct = ((t.boxes - t.prevBoxes) / t.prevBoxes) * 100;
    delta = (
      <span style={{ fontWeight: 700, color: pct >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
        {pct >= 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}% <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>vs last month ({fmt(t.prevBoxes)})</span>
      </span>
    );
  }

  const openDay = (idx) => {
    const d = data && data.days[idx];
    if (!d || d.boxes <= 0) return;
    const dd = String(d.day).padStart(2, '0');
    push({ group: 'customer', title: `${dd}/${ym.slice(5, 7)}/${ym.slice(0, 4)} — customer-wise dispatch`, query: { date: `${ym}-${dd}` } });
  };
  // SIZE-CUST v1: a size opens the customer list first (one row per customer); a customer then opens the full detail
  const openSize = (s) => { if (s && s.id) push({ group: 'customer', title: `${s.name} — customer-wise dispatch (${monthName})`, query: { sizeId: s.id } }); };
  const openCustomer = (c) => push({ group: 'detail', title: `${c.name} — ${monthName}`, query: { customerId: c.id } });
  const openDayCustomer = (r) => push({
    group: 'detail',
    title: `${r.customer} — ${drill.query.date ? dmy(drill.query.date) : drill.title.split(' — ')[0]}`,
    query: { ...drill.query, customerId: r.customerId },
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
        <h1 style={{ margin: 0 }}>{title} — {monthName}</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-secondary" onClick={() => go(shift(ym, -1))}>◀ Previous</button>
          <button type="button" className="btn btn-secondary" onClick={() => go(shift(ym, 1))} disabled={ym >= thisYm}>Next ▶</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>Back to Dashboard</button>
        </div>
      </div>

      {err && <Card><p style={{ margin: 0, color: 'var(--color-danger)' }}>{err}</p></Card>}
      {!data && !err && <Card><p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Loading…</p></Card>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            <Stat label="Total dispatch (box)" value={fmt(t.boxes)} sub={delta} />
            <Stat label="Customers" value={fmt(t.customers)} />
            <Stat label="Dispatch days" value={fmt(t.activeDays)} />
            <Stat label="Avg per dispatch day" value={fmt(t.avgPerActiveDay)} />
          </div>

          <Card title="Day-wise dispatch" style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ width: '100%', height: 280, ...CLICKABLE }}>
              <ResponsiveContainer>
                <BarChart
                  data={data.days.map((d) => ({ label: String(d.day), Box: d.boxes }))}
                  margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                  onClick={(st) => { if (st && st.activeTooltipIndex != null) openDay(Number(st.activeTooltipIndex)); }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                  <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={{ stroke: 'var(--color-border)' }} interval={0} />
                  <YAxis tick={tick} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip formatter={(v) => [fmt(v), 'Box']} labelFormatter={(l) => `${l} ${MONTHS[Number(ym.slice(5, 7)) - 1]}`} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Box" fill="#0E6F8A" radius={[3, 3, 0, 0]} maxBarSize={22} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={HINT}>Click a day to see that day&apos;s customers, then a customer for the detail.</div>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <Card title="Size-wise dispatch" style={{ marginBottom: 0 }}>
              {data.sizes.length === 0 ? <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>No dispatch.</p> : (
                <>
                  <div style={{ width: '100%', height: Math.max(160, data.sizes.length * 40 + 30) }}>
                    <ResponsiveContainer>
                      <BarChart data={data.sizes} layout="vertical" margin={{ top: 0, right: 48, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                        <XAxis type="number" tick={tick} tickLine={false} axisLine={false} />
                        <YAxis type="category" dataKey="name" width={90} tick={tick} tickLine={false} axisLine={false} />
                        <Tooltip formatter={(v) => [fmt(v), 'Box']} contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="boxes" radius={[0, 4, 4, 0]} maxBarSize={22} cursor="pointer" onClick={(d) => openSize(d)}>
                          {data.sizes.map((s, i) => <Cell key={s.name} fill={PALETTE[i % PALETTE.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={HINT}>Click a size to see its dispatch (customer, design, batch/shade).</div>
                </>
              )}
            </Card>

            {data.parties.length > 0 && (
              <Card title="Ref. Party-wise" style={{ marginBottom: 0 }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={TH}>Ref. Party</th><th style={{ ...TH, textAlign: 'right' }}>Box</th><th style={{ ...TH, textAlign: 'right' }}>Customers</th></tr></thead>
                    <tbody>
                      {data.parties.map((p) => (
                        <tr key={p.id} style={CLICKABLE} onClick={() => navigate(`/ref-party-month?month=${ym}&party=${p.id}`)}>
                          <td style={TD}>{p.name}</td>
                          <td style={{ ...TD, textAlign: 'right' }}>{fmt(p.boxes)}</td>
                          <td style={{ ...TD, textAlign: 'right' }}>{fmt(p.customers)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>

          <Card title={`Customer-wise (${data.customers.length})`} style={{ marginBottom: 'var(--space-4)' }}>
            <input value={cq} onChange={(e) => setCq(e.target.value)} placeholder="Search customer / city / size" style={{ ...FIELD, width: '100%', maxWidth: 320, marginBottom: 8 }} />
            <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }} className="table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={TH}>#</th><th style={TH}>Customer</th><th style={TH}>City</th>
                    <th style={TH}>Size</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Box</th><th style={{ ...TH, textAlign: 'right' }}>% share</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Dispatch lines</th><th style={TH}>Last dispatch</th><th style={TH} />
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 && <tr><td style={TD} colSpan={9}>No customers.</td></tr>}
                  {customers.map((c, i) => (
                    <tr key={c.id} style={CLICKABLE} title="Click to see this customer's dispatch" onClick={() => openCustomer(c)}>
                      <td style={TD}>{i + 1}</td>
                      <td style={{ ...TD, fontWeight: 600 }}>{c.name}</td>
                      <td style={TD}>{c.city || '—'}</td>
                      <td style={TD} title={c.sizes}>{clip(c.sizes, 36) || '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(c.boxes)}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{t.boxes ? ((c.boxes / t.boxes) * 100).toFixed(1) : '0.0'}%</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{fmt(c.lines)}</td>
                      <td style={TD}>{dmy(c.lastDate)}</td>
                      <td style={{ ...TD, color: 'var(--color-text-muted)' }}>›</td>
                    </tr>
                  ))}
                </tbody>
                {customers.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={TF} colSpan={4}>Grand Total ({customers.length} customers)</td>
                      <td style={{ ...TF, textAlign: 'right' }}>{fmt(custBoxes)}</td>
                      <td style={{ ...TF, textAlign: 'right' }}>{t.boxes ? ((custBoxes / t.boxes) * 100).toFixed(1) : '0.0'}%</td>
                      <td style={{ ...TF, textAlign: 'right' }}>{fmt(custLines)}</td>
                      <td style={TF} colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div style={HINT}>Click any customer row to see that customer&apos;s dispatch.</div>
          </Card>

          {data.designsAvailable && (
            <Card title={`Design-wise (${data.designs.length})`} style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <button type="button" className={`btn ${sizeTab === '' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSizeTab('')}>All ({data.designs.length})</button>
                {sizeTabs.map((s) => (
                  <button key={s.id} type="button" className={`btn ${sizeTab === s.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSizeTab(s.id)}>
                    {s.name} ({s.count})
                  </button>
                ))}
              </div>
              <input value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Search design" style={{ ...FIELD, width: '100%', maxWidth: 320, marginBottom: 8 }} />
              <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }} className="table-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={TH}>#</th><th style={TH}>Design</th><th style={TH}>Size</th><th style={{ ...TH, textAlign: 'right' }}>Box</th><th style={{ ...TH, textAlign: 'right' }}>Customers</th></tr></thead>
                  <tbody>
                    {designs.length === 0 && <tr><td style={TD} colSpan={5}>No designs.</td></tr>}
                    {designs.map((d, i) => (
                      <tr key={`${d.sizeId}-${d.name}`}>
                        <td style={TD}>{i + 1}</td><td style={TD}>{d.design}</td><td style={TD}>{d.size}</td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{fmt(d.boxes)}</td>
                        <td style={{ ...TD, textAlign: 'right' }}>{fmt(d.customers)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {designs.length > 0 && (
                    <tfoot><tr><td style={TF} colSpan={3}>Grand Total</td><td style={{ ...TF, textAlign: 'right' }}>{fmt(designBoxes)}</td><td style={TF} /></tr></tfoot>
                  )}
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {drill && (
        <DrillModal
          key={JSON.stringify([drill.group, drill.query])}
          drill={drill}
          month={ym}
          party={party}
          onClose={() => setStack([])}
          onBack={stack.length > 1 ? () => setStack((s) => s.slice(0, -1)) : null}
          onOpenCustomer={openDayCustomer}
        />
      )}
    </div>
  );
}
